// Admin Emails page -- iteration 076
// Super-admin only. Lists sent emails with preview capability.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as api from "@/lib/api";
import type { EmailLogPublic } from "@dugads/shared";

const STATUS_COLORS: Record<string, string> = {
  queued: "bg-yellow-100 text-yellow-800",
  sent: "bg-blue-100 text-blue-800",
  delivered: "bg-green-100 text-green-800",
  bounced: "bg-red-100 text-red-800",
  failed: "bg-red-100 text-red-800",
};

export default function AdminEmailsPage() {
  const location = useLocation();
  const [emails, setEmails] = useState<EmailLogPublic[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .getEmailLogs({ page, page_size: 20, status: statusFilter || undefined, search: search || undefined })
      .then((res) => {
        setEmails(res.data);
        setTotal(res.total);
        setError("");
      })
      .catch((err) => setError(err.message ?? "Failed to load emails"))
      .finally(() => setLoading(false));
  }, [location.key, page, statusFilter, search]);

  return (
    <div data-page="admin-emails" className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Email Log</h1>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search by email or subject..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="rounded-md border px-3 py-1.5 text-sm bg-background w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-md border px-3 py-1.5 text-sm bg-background"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {error && <p className="text-destructive mb-4">{error}</p>}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : emails.length === 0 ? (
        <p className="text-muted-foreground">No emails found.</p>
      ) : (
        <>
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">To</th>
                  <th className="text-left px-3 py-2 font-medium">Subject</th>
                  <th className="text-left px-3 py-2 font-medium">Template</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Preview</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.id} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono text-xs">{email.to_email}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate">{email.subject}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                        {email.template}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[email.status] ?? ""}`}>
                        {email.status}
                      </span>
                      {email.error_message && (
                        <p className="text-xs text-muted-foreground mt-0.5 max-w-[200px] truncate" title={email.error_message}>
                          {email.error_message}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(email.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      {email.has_preview && (
                        <button
                          type="button"
                          data-action={`preview-${email.id}`}
                          onClick={() => setPreviewId(previewId === email.id ? null : email.id)}
                          className="text-xs text-primary hover:underline"
                        >
                          {previewId === email.id ? "Close" : "Preview"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
            <span>{total} email{total !== 1 ? "s" : ""}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-2 py-1 border rounded text-xs disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-2 py-1 text-xs">Page {page}</span>
              <button
                type="button"
                disabled={page * 20 >= total}
                onClick={() => setPage(page + 1)}
                className="px-2 py-1 border rounded text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Email preview panel */}
      {previewId && (
        <div className="mt-4 border rounded-md overflow-hidden">
          <div className="bg-muted/50 px-3 py-2 flex items-center justify-between border-b">
            <span className="text-sm font-medium">Email Preview</span>
            <button
              type="button"
              onClick={() => setPreviewId(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          <iframe
            src={`/api/admin/emails/${previewId}/preview`}
            title="Email preview"
            className="w-full bg-white"
            style={{ height: "500px", border: "none" }}
            sandbox="allow-same-origin"
          />
        </div>
      )}
    </div>
  );
}
